import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Paper, Typography, Button, IconButton, Chip, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, MenuItem, FormControl, InputLabel, Tooltip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockIcon from '@mui/icons-material/Lock';
import TimelineIcon from '@mui/icons-material/Timeline';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import {
  getTires, deleteTire, TIRE_STATUSES, isNearEndOfLife,
} from '../../utils/tiresService';
import TireForm from './TireForm';
import TireLifingForm from './TireLifingForm';

export default function TireManagement({ teamId, userRole, userId }) {
  const { t } = useTranslation('tires');
  const [tires, setTires]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [vehicles, setVehicles] = useState([]);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing]   = useState(null);

  const [delOpen, setDelOpen]   = useState(false);
  const [deleting, setDeleting] = useState(null);

  const [lifingOpen, setLifingOpen]   = useState(false);
  const [lifingTire, setLifingTire]   = useState(null);

  // Filtri
  const [search, setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBrand, setFilterBrand]   = useState('all');
  const [filterVehicle, setFilterVehicle] = useState('all');

  // ── Caricamento iniziale ──────────────────────────────────
  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [tireList, vehSnap] = await Promise.all([
        getTires(teamId),
        getDocs(query(collection(db, 'vehicles'), where('teamId', '==', teamId))),
      ]);
      setTires(tireList);
      setVehicles(vehSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setError(e.message || t('form_error_load'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (teamId) loadAll(); }, [teamId]); // eslint-disable-line

  const vehicleMap = useMemo(() => {
    const m = {};
    vehicles.forEach(v => { m[v.id] = v.modello || v.name || v.id; });
    return m;
  }, [vehicles]);

  // ── Filtraggio ──────────────────────────────────────────────
  const filtered = useMemo(() => {
    return tires.filter(tr => {
      if (filterStatus !== 'all' && tr.status !== filterStatus) return false;
      if (filterBrand !== 'all'  && tr.brand !== filterBrand)   return false;
      if (filterVehicle !== 'all' && (tr.vehicleId || '') !== (filterVehicle === '_none' ? '' : filterVehicle)) return false;
      if (search) {
        const s = search.toLowerCase();
        const hay = `${tr.barcode} ${tr.brand} ${tr.compound} ${tr.dotCode} ${tr.notes}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [tires, filterStatus, filterBrand, filterVehicle, search]);

  const brands = useMemo(() => {
    const set = new Set(tires.map(tr => tr.brand).filter(Boolean));
    return [...set].sort();
  }, [tires]);

  const isManager = userRole === 'admin' || userRole === 'Engineer';
  if (!isManager) {
    return <Alert severity="warning">{t('access_warning')}</Alert>;
  }

  const handleEdit   = (tr) => { setEditing(tr); setFormOpen(true); };
  const handleAdd    = ()  => { setEditing(null); setFormOpen(true); };
  const handleDelete = (tr) => { setDeleting(tr); setDelOpen(true); };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteTire(deleting.id);
      setTires(prev => prev.filter(x => x.id !== deleting.id));
      setDelOpen(false);
      setDeleting(null);
    } catch (e) {
      alert(e.message || t('form_error_delete'));
    }
  };

  const statusChip = (s) => {
    const cfg = TIRE_STATUSES.find(x => x.key === s) || { color: '#999' };
    return <Chip label={t(`status_${s}`, s)} size="small" sx={{ bgcolor: cfg.color, color: '#fff', fontWeight: 600 }} />;
  };

  const deleteDescr = deleting?.brand ? ` (${deleting.brand} ${deleting.compound || ''})` : '';

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" fontWeight={700}>{t('page_title')}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          {t('add_tire')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filtri */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          label={t('filter_search')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('filter_status')}</InputLabel>
          <Select value={filterStatus} label={t('filter_status')} onChange={e => setFilterStatus(e.target.value)}>
            <MenuItem value="all">{t('filter_all_m')}</MenuItem>
            {TIRE_STATUSES.map(s => <MenuItem key={s.key} value={s.key}>{t(`status_${s.key}`, s.key)}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('filter_brand')}</InputLabel>
          <Select value={filterBrand} label={t('filter_brand')} onChange={e => setFilterBrand(e.target.value)}>
            <MenuItem value="all">{t('filter_all_f')}</MenuItem>
            {brands.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('filter_vehicle')}</InputLabel>
          <Select value={filterVehicle} label={t('filter_vehicle')} onChange={e => setFilterVehicle(e.target.value)}>
            <MenuItem value="all">{t('filter_all_f')}</MenuItem>
            <MenuItem value="_none">{t('filter_unassigned')}</MenuItem>
            {vehicles.map(v => <MenuItem key={v.id} value={v.id}>{vehicleMap[v.id]}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {t('count_label', { shown: filtered.length, total: tires.length })}
        </Typography>
      </Paper>

      {/* Tabella gomme */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>{t('col_barcode')}</strong></TableCell>
                <TableCell><strong>{t('col_brand')}</strong></TableCell>
                <TableCell><strong>{t('col_compound')}</strong></TableCell>
                <TableCell><strong>{t('col_dot')}</strong></TableCell>
                <TableCell><strong>{t('col_status')}</strong></TableCell>
                <TableCell><strong>{t('col_vehicle')}</strong></TableCell>
                <TableCell><strong>{t('col_position')}</strong></TableCell>
                <TableCell align="right"><strong>{t('col_km')}</strong></TableCell>
                <TableCell align="right"><strong>{t('col_cycles')}</strong></TableCell>
                <TableCell align="center"><strong>{t('col_actions')}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={10} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  {tires.length ? t('row_empty_no_match') : t('row_empty_total')}
                </TableCell></TableRow>
              ) : filtered.map(tr => {
                const nearEol = isNearEndOfLife(tr);
                return (
                  <TableRow key={tr.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{tr.barcode}</TableCell>
                    <TableCell>{tr.brand || '—'}</TableCell>
                    <TableCell>{tr.compound || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{tr.dotCode || '—'}</TableCell>
                    <TableCell>
                      {statusChip(tr.status)}
                      {tr.sealed && <Tooltip title={t('tooltip_sealed')}><LockIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle', color: '#673ab7' }} /></Tooltip>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{tr.vehicleId ? (vehicleMap[tr.vehicleId] || '?') : '—'}</TableCell>
                    <TableCell>{tr.position || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: nearEol ? 700 : 400, color: nearEol ? 'warning.main' : 'inherit' }}>
                      {tr.kmTotal}
                      {tr.lifeMaxKm ? <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>/ {tr.lifeMaxKm}</Typography> : null}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: nearEol ? 700 : 400, color: nearEol ? 'warning.main' : 'inherit' }}>
                      {tr.heatCycles}
                      {tr.lifeMaxCycles ? <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>/ {tr.lifeMaxCycles}</Typography> : null}
                    </TableCell>
                    <TableCell align="center">
                      {nearEol && <Tooltip title={t('tooltip_near_eol')}><WarningAmberIcon sx={{ fontSize: 18, color: 'warning.main', mr: 0.5, verticalAlign: 'middle' }} /></Tooltip>}
                      <Tooltip title={t('tooltip_lifing')}>
                        <IconButton size="small" color="secondary" onClick={() => { setLifingTire(tr); setLifingOpen(true); }}>
                          <TimelineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" color="primary" onClick={() => handleEdit(tr)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(tr)}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TireForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={loadAll}
        tire={editing}
        teamId={teamId}
        userId={userId}
        vehicles={vehicles}
      />

      <TireLifingForm
        open={lifingOpen}
        onClose={() => { setLifingOpen(false); setLifingTire(null); }}
        onSaved={loadAll}
        tires={lifingTire ? [lifingTire] : []}
      />

      <Dialog open={delOpen} onClose={() => setDelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main', fontWeight: 700 }}>{t('delete_confirm_title')}</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            dangerouslySetInnerHTML={{
              __html: t('delete_confirm_text', { barcode: deleting?.barcode || '', descr: deleteDescr })
                + '<br/>' + t('delete_confirm_history'),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDelOpen(false)}>{t('cancel')}</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>{t('delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
